/**
 * WarehouseItemsPage — Stored Items (Phase 3a, read only)
 * ───────────────────────────────────────────────────────
 * Site picker (WAREHOUSE-module sites) + current-occupancy table + filters +
 * row-click detail panel. Role-gated to ADMIN / FIELD_REP (the warehouse-write
 * roles); a client-viewer affordance is deferred. No Receive/Ship/Move here —
 * that's 3b.
 *
 * Filtering is client-side over the backend's default current-occupancy set, so
 * the filter dropdowns stay fully populated (options derive from the loaded set
 * via getUniqueValues). asset_type options come from ReferenceData; status /
 * contract / location options are derived from the data (no hardcoded picker).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock } from 'lucide-react';
import { useUser } from '../../../shared/context/UserContext';
import { useReferenceData } from '../../../shared/hooks/useReferenceData';
import { useGridState, sortRecords } from '../../../components/GridView/useGridState';
import { getSites } from '../../inventory/api/inventoryApi';
import { getWarehouseItems, setDisposition } from '../api/warehouseApi';
import { WAREHOUSE_COLUMNS } from '../warehouseColumnManifest';
import WarehouseItemDetailPanel from '../components/WarehouseItemDetailPanel';
import '../warehouse.css';

const WAREHOUSE_VIEW_ROLES = new Set(['ADMIN', 'FIELD_REP']);

function siteLabel(s) {
  if (s.site_city) return `${s.site_name} (${s.site_city})`;
  return s.site_name || 'Unnamed Site';
}

function joinLoc(item) {
  const parts = [item.building, item.section, item.row, item.stack].filter(Boolean);
  return parts.length ? parts.join(' / ') : '';
}

function getUniqueValues(rows, field) {
  return [...new Set(rows.map((r) => r[field]).filter((v) => v !== null && v !== undefined && v !== ''))].sort();
}

/* Cell formatting per column type (read-only). Raw strings for dates — no Date(). */
function cellText(item, col) {
  const v = item[col.key];
  if (col.type === 'boolean') return v === true ? 'Yes' : v === false ? 'No' : '—';
  if (v === null || v === undefined || v === '') return '—';
  return String(v);
}

/* Dwell helpers — values are numeric years (no Date()). */
function fmtYears(v) {
  return (v === null || v === undefined) ? null : `${Number(v).toFixed(1)}y`;
}
/* "X.Xy / Y.Yy" | "X.Xy / —" | "On loan" (null dwell_years = no open stay; never "0"). */
function formatDwellProgress(it) {
  const d = fmtYears(it.dwell_years);
  if (d === null) return 'On loan';
  const t = fmtYears(it.threshold_years);
  return `${d} / ${t === null ? '—' : t}`;
}
/* Review/disposition label precedence (per-row fields already in the list payload). */
function reviewLabel(it) {
  if (it.needs_review === true) return 'Review';
  if (it.dwell_disposition_status === 'IN_DISPOSITION') return 'In disposition';
  if (it.dwell_disposition_status === 'RETAINED') return 'Retained';
  return '';
}

/* Token-based badge (mirrors the disposition gap-badge pill; var(--token) only). */
const BADGE_BASE = {
  display: 'inline-flex', alignItems: 'center',
  padding: 'var(--space-1) var(--space-4)', borderRadius: 'var(--radius-pill)',
  fontSize: 'var(--text-2xs)', fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap',
};
const BADGE_VARIANTS = {
  Review: { background: 'var(--color-warning-light)', color: 'var(--color-warning-dark)' },
  'In disposition': { background: 'var(--color-bg-subtle)', color: 'var(--color-text-secondary)' },
  Retained: { color: 'var(--color-text-muted)' },
};
function ReviewBadge({ value }) {
  if (!value) return <span className="wh-empty">—</span>;
  return <span style={{ ...BADGE_BASE, ...(BADGE_VARIANTS[value] || {}) }}>{value}</span>;
}

/* Custom cell render for the derived/badge columns; falls back to cellText. */
function renderCell(item, col) {
  if (col.key === 'dwell_progress') {
    return (
      <span style={item.dwell_crossed ? { color: 'var(--color-warning-dark)', fontWeight: 600 } : undefined}>
        {item.dwell_progress || '—'}
      </span>
    );
  }
  if (col.key === 'review') return <ReviewBadge value={item.review} />;
  return cellText(item, col);
}

export default function WarehouseItemsPage() {
  const { callerEmail, userProfile } = useUser();
  const role = userProfile?.role;
  const isAdmin = role === 'ADMIN';
  const navigate = useNavigate();

  const { columnVisibility, sortKeys, setSortKeys } = useGridState(
    callerEmail, 'warehouse', WAREHOUSE_COLUMNS
  );

  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [sitesLoading, setSitesLoading] = useState(true);

  const [items, setItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);

  /* Filters (client-side over the loaded occupancy set) */
  const [fContract, setFContract] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fAssetType, setFAssetType] = useState('');
  const [fVerified, setFVerified] = useState('');
  const [fHold, setFHold] = useState('');
  const [fLocation, setFLocation] = useState('');
  const [fNeedsReview, setFNeedsReview] = useState(false);
  const [groupByContract, setGroupByContract] = useState(true);
  const [q, setQ] = useState('');

  /* Multi-select → disposition handoff (ADMIN-only). Set of warehouse_item_id. */
  const [selected, setSelected] = useState(new Set());
  const [dispoSubmitting, setDispoSubmitting] = useState(false);
  const [dispoToast, setDispoToast] = useState(null);

  const { options: refOptions } = useReferenceData(['asset_type'], callerEmail, selectedSite || null);

  /* Sites (WAREHOUSE module only) */
  useEffect(() => {
    if (!callerEmail) return;
    setSitesLoading(true);
    getSites(callerEmail, 'WAREHOUSE')
      .then((data) => {
        const list = Array.isArray(data) ? data : data.sites || [];
        setSites(list);
        if (list.length === 1) setSelectedSite(list[0].site_id);
      })
      .catch(() => {})
      .finally(() => setSitesLoading(false));
  }, [callerEmail]);

  /* Items (backend default occupancy set; client filters refine below).
     loadItems is reusable so the detail panel can refresh the grid after a
     write without dropping the current selection. */
  const loadItems = useCallback(() => {
    if (!selectedSite || !callerEmail) { setItems([]); return Promise.resolve(); }
    setItemsLoading(true); setItemsError(null);
    return getWarehouseItems(callerEmail, { siteId: selectedSite })
      .then((list) => setItems(list.map((it) => ({
        ...it,
        location: joinLoc(it),
        dwell_progress: formatDwellProgress(it),
        review: reviewLabel(it),
      }))))
      .catch((err) => setItemsError(err.message))
      .finally(() => setItemsLoading(false));
  }, [selectedSite, callerEmail]);

  useEffect(() => {
    setSelectedId(null); // reset selection only when the site/caller changes
    setSelected(new Set()); // drop any multi-select when the site changes
    loadItems();
  }, [loadItems]);

  const contractOpts = useMemo(() => getUniqueValues(items, 'contract_id'), [items]);
  const statusOpts = useMemo(() => getUniqueValues(items, 'warehouse_status'), [items]);
  const locationOpts = useMemo(() => getUniqueValues(items, 'location'), [items]);
  const assetTypeOpts = refOptions.asset_type || [];

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return items.filter((it) => {
      if (fContract && it.contract_id !== fContract) return false;
      if (fStatus && it.warehouse_status !== fStatus) return false;
      if (fAssetType && it.asset_type !== fAssetType) return false;
      if (fVerified && String(!!it.verified) !== fVerified) return false;
      if (fHold && String(!!it.hold) !== fHold) return false;
      if (fLocation && it.location !== fLocation) return false;
      if (fNeedsReview && !it.needs_review) return false;
      if (ql) {
        const hay = [it.warehouse_item_id, it.asset_type, it.contract_id, it.contract?.customer_po,
          it.linked_tool_id, it.location].filter(Boolean).join(' ').toLowerCase();
        if (!hay.includes(ql)) return false;
      }
      return true;
    });
  }, [items, fContract, fStatus, fAssetType, fVerified, fHold, fLocation, fNeedsReview, q]);

  const colTypeMap = useMemo(() => {
    const m = {}; for (const c of WAREHOUSE_COLUMNS) m[c.key] = c.type; return m;
  }, []);
  const sorted = useMemo(() => sortRecords(filtered, sortKeys, colTypeMap), [filtered, sortKeys, colTypeMap]);

  /* Summary counts over the currently-filtered set. */
  const reviewCount = useMemo(() => filtered.filter((it) => it.needs_review).length, [filtered]);
  const inDispositionCount = useMemo(
    () => filtered.filter((it) => it.dwell_disposition_status === 'IN_DISPOSITION').length,
    [filtered],
  );

  /* Contract grouping (default on): bucket the sorted rows by contract_id, label
     from the contract block, count per-group needs-review. '__none__' sorts last. */
  const groupedRows = useMemo(() => {
    if (!groupByContract) return [];
    const map = new Map();
    for (const it of sorted) {
      const key = it.contract_id || '__none__';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(it);
    }
    const entries = [...map.entries()].map(([key, rows]) => ({
      key,
      label: rows[0]?.contract?.customer_po || (key === '__none__' ? 'No contract' : key),
      rows,
      needReview: rows.filter((r) => r.needs_review).length,
    }));
    entries.sort((a, b) => {
      if (a.key === '__none__') return 1;
      if (b.key === '__none__') return -1;
      return String(a.key).localeCompare(String(b.key));
    });
    return entries;
  }, [groupByContract, sorted]);

  const visibleColumns = WAREHOUSE_COLUMNS.filter((c) => columnVisibility[c.key] !== false);

  /* Single data-row renderer — shared by the flat and grouped table bodies.
     (Plain function returning JSX; not a nested component.) */
  const renderRow = (item) => (
    <tr
      key={item.warehouse_item_id}
      className={`wh-row${selectedId === item.warehouse_item_id ? ' wh-row-selected' : ''}`}
      onClick={() => setSelectedId(item.warehouse_item_id)}
    >
      {isAdmin && (
        <td className="wh-td" onClick={(e) => e.stopPropagation()}>
          {item.linked_tool_id ? (
            <input
              type="checkbox"
              checked={selected.has(item.warehouse_item_id)}
              onChange={() => toggleSelect(item.warehouse_item_id)}
              aria-label="Select item for disposition"
            />
          ) : null}
        </td>
      )}
      {visibleColumns.map((col) => (
        <td key={col.key} className="wh-td">{renderCell(item, col)}</td>
      ))}
    </tr>
  );

  /* Column count incl. the ADMIN checkbox column (for group/empty colSpan). */
  const totalCols = visibleColumns.length + (isAdmin ? 1 : 0);

  /* Multi-select (ADMIN handoff). Only tool-linked rows are selectable —
     a NONE-link item has nothing to route into disposition. */
  const selectableIds = useMemo(
    () => filtered.filter((it) => it.linked_tool_id).map((it) => it.warehouse_item_id),
    [filtered],
  );
  const allSelectableSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));
  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (selectableIds.length && selectableIds.every((id) => prev.has(id))) {
        selectableIds.forEach((id) => next.delete(id));
      } else {
        selectableIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  /* Start disposition: stamp the selected WAREHOUSE_ITEM_IDs IN_DISPOSITION (await
     the audited write), then hand off to /admin/dispositions BY linked_tool_id —
     two distinct id spaces. dwell_override carries only the needs_review subset. */
  const handleStartDisposition = useCallback(async () => {
    const rows = items.filter((it) => selected.has(it.warehouse_item_id));
    const wids = rows.map((r) => r.warehouse_item_id);
    if (!wids.length) return;
    const tool_ids = rows.map((r) => r.linked_tool_id).filter(Boolean);
    const dwell_override_tool_ids = rows
      .filter((r) => r.needs_review === true)
      .map((r) => r.linked_tool_id)
      .filter(Boolean);
    setDispoSubmitting(true);
    setDispoToast(null);
    try {
      await setDisposition(callerEmail, {
        siteId: selectedSite, warehouseItemIds: wids, dispositionStatus: 'IN_DISPOSITION',
      });
      setSelected(new Set());
      navigate('/admin/dispositions', { state: { tool_ids, dwell_override_tool_ids } });
    } catch (err) {
      setDispoToast(err.message || 'Failed to start disposition.');
    } finally {
      setDispoSubmitting(false);
    }
  }, [items, selected, callerEmail, selectedSite, navigate]);

  function toggleSort(key) {
    setSortKeys((prev) => {
      const cur = prev.find((s) => s.field === key);
      if (!cur) return [{ field: key, direction: 'asc' }];
      if (cur.direction === 'asc') return [{ field: key, direction: 'desc' }];
      return [];
    });
  }
  const sortDir = (key) => sortKeys.find((s) => s.field === key)?.direction;

  /* Role gate — ADMIN / FIELD_REP only (digitization Lock pattern) */
  if (role && !WAREHOUSE_VIEW_ROLES.has(role)) {
    return (
      <div className="wh-lock">
        <Lock size={48} />
        <h1>You do not have access</h1>
      </div>
    );
  }

  const selectedSiteObj = sites.find((s) => s.site_id === selectedSite);

  return (
    <div className="wh-page">
      <div className="wh-header">
        <h1 className="wh-header-title">Stored Items</h1>
        <select
          className="wh-site-select"
          value={selectedSite}
          onChange={(e) => setSelectedSite(e.target.value)}
          disabled={sitesLoading}
        >
          <option value="">Select a warehouse site…</option>
          {sites.map((s) => <option key={s.site_id} value={s.site_id}>{siteLabel(s)}</option>)}
        </select>
        {selectedSiteObj && <span className="wh-header-meta">{filtered.length} of {items.length} in occupancy</span>}
        <span className="wh-header-spacer" />
        {isAdmin && selected.size > 0 && (
          <button
            className="wh-receive-btn"
            type="button"
            disabled={dispoSubmitting}
            onClick={handleStartDisposition}
          >
            {dispoSubmitting ? 'Starting…' : `Start disposition (${selected.size})`}
          </button>
        )}
        <button className="wh-receive-btn" type="button" onClick={() => navigate('/warehouse/receive')}>+ Receive</button>
      </div>

      {dispoToast && <div className="wh-form-error">{dispoToast}</div>}

      {selectedSite && (
        <div className="wh-filterbar">
          <input className="wh-filter-input" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="wh-filter-select" value={fContract} onChange={(e) => setFContract(e.target.value)}>
            <option value="">All Contracts</option>
            {contractOpts.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className="wh-filter-select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
            <option value="">All Statuses</option>
            {statusOpts.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className="wh-filter-select" value={fAssetType} onChange={(e) => setFAssetType(e.target.value)}>
            <option value="">All Asset Types</option>
            {assetTypeOpts.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className="wh-filter-select" value={fLocation} onChange={(e) => setFLocation(e.target.value)}>
            <option value="">All Locations</option>
            {locationOpts.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <select className="wh-filter-select" value={fVerified} onChange={(e) => setFVerified(e.target.value)}>
            <option value="">Verified: Any</option>
            <option value="true">Verified: Yes</option>
            <option value="false">Verified: No</option>
          </select>
          <select className="wh-filter-select" value={fHold} onChange={(e) => setFHold(e.target.value)}>
            <option value="">Hold: Any</option>
            <option value="true">Hold: Yes</option>
            <option value="false">Hold: No</option>
          </select>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
            <input type="checkbox" checked={fNeedsReview} onChange={(e) => setFNeedsReview(e.target.checked)} />
            Needs review
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
            <input type="checkbox" checked={groupByContract} onChange={(e) => setGroupByContract(e.target.checked)} />
            Group by contract
          </label>
        </div>
      )}

      <div className="wh-body">
        <div className={`wh-list${selectedId ? ' wh-list-split' : ''}`}>
          {!selectedSite && <div className="wh-empty-pane">Select a warehouse site to view stored items.</div>}
          {selectedSite && itemsLoading && <div className="wh-empty-pane">Loading…</div>}
          {selectedSite && itemsError && <div className="wh-empty-pane wh-error">{itemsError}</div>}
          {selectedSite && !itemsLoading && !itemsError && (
            <>
              <div
                className="wh-summary"
                style={{
                  padding: 'var(--space-3) var(--space-5)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-secondary)',
                  borderBottom: '1px solid var(--color-border-light)',
                }}
              >
                {filtered.length} items · {reviewCount} need review · {inDispositionCount} in disposition
              </div>
              <table className="wh-table">
                <thead>
                  <tr>
                    {isAdmin && (
                      <th className="wh-th" style={{ cursor: 'default' }} onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={allSelectableSelected}
                          onChange={toggleAll}
                          aria-label="Select all tool-linked items"
                        />
                      </th>
                    )}
                    {visibleColumns.map((col) => (
                      <th key={col.key} className="wh-th" onClick={() => toggleSort(col.key)}>
                        {col.label}{sortDir(col.key) === 'asc' ? ' ▲' : sortDir(col.key) === 'desc' ? ' ▼' : ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sorted.length === 0 ? (
                    <tr><td className="wh-td wh-empty" colSpan={totalCols}>No items match.</td></tr>
                  ) : groupByContract ? (
                    groupedRows.map((g) => (
                      <React.Fragment key={g.key}>
                        <tr>
                          <td
                            className="wh-td"
                            colSpan={totalCols}
                            style={{ background: 'var(--color-bg-subtle)', fontWeight: 600, color: 'var(--color-text-heading)' }}
                          >
                            {g.label} · {g.rows.length} items, {g.needReview} need review
                          </td>
                        </tr>
                        {g.rows.map(renderRow)}
                      </React.Fragment>
                    ))
                  ) : (
                    sorted.map(renderRow)
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>

        {selectedId && (
          <div className="wh-detail-pane">
            <WarehouseItemDetailPanel
              callerEmail={callerEmail}
              siteId={selectedSite}
              warehouseItemId={selectedId}
              onClose={() => setSelectedId(null)}
              onChanged={loadItems}
            />
          </div>
        )}
      </div>
    </div>
  );
}
